import React, { useState, useRef } from "react";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIMultiPartHeaders } from "../../../common/Utils";
import CustomModal from "../../../core/common/customModal";
import PreLoader from "../../../core/common/preLoader";
import CommonSelectV2, { Option } from "../../../core/common/commonSelectV2";

// Priority options for ticket creation
const PRIORITY_OPTIONS: Option[] = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
  { value: "URGENT", label: "Urgent" }
];

// Title options for ticket creation
const TITLE_OPTIONS: Option[] = [
  { value: "IT Support", label: "IT Support" },
  { value: "Account Issues", label: "Account Issues" },
  { value: "Course Access", label: "Course Access" },
  { value: "Payment Issues", label: "Payment Issues" },
  { value: "Technical Support", label: "Technical Support" },
  { value: "General Inquiry", label: "General Inquiry" }
];

interface CreateTicketModalProps {
  show: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  show,
  onClose,
  onTicketCreated,
}) => {
  // Form state
  const [selectedTitle, setSelectedTitle] = useState<Option>(TITLE_OPTIONS[0]); // Default to IT Support
  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedPriority, setSelectedPriority] = useState<Option>(PRIORITY_OPTIONS[1]); // Default to Medium
  const [files, setFiles] = useState<any[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { callFetch , isLoading} = useFetch();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles:any = e.target.files;
    if (selectedFiles && selectedFiles.length > 2) {
      setErrorMessage("You can only upload a maximum of 2 files");
      e.target.value = "";
      return;
    }
    setFiles(selectedFiles);
    setErrorMessage("");
  };

  const removeFiles = () => {
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setSelectedTitle(TITLE_OPTIONS[0]);
    setSubject("");
    setDescription("");
    setSelectedPriority(PRIORITY_OPTIONS[1]);
    setFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTitle || !subject || !description || !selectedPriority) {
      setErrorMessage("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const formData:any = new FormData();
      formData.append("title", selectedTitle.value);
      formData.append("subject", subject.trim());
      formData.append("priority", selectedPriority.value);
      formData.append("description", description.trim());

      if (files) {
        for (let i = 0; i < files.length; i++) {
          formData.append("screenshots", files[i]);
        }
      }

      // Make the API call
      await callFetch(
        API.CREATE_SUPPORT_TICKET,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      setSuccessMessage("Ticket created successfully!");
      resetForm();

      // Trigger the onTicketCreated callback if provided
      if (onTicketCreated) {
        onTicketCreated();
      }

      // Auto close the modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error creating ticket:", error);
      setErrorMessage("Failed to create ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = (
    <>
      <i className="ti ti-plus me-2"></i>
      Create Support Ticket
    </>
  );

  const modalBodyContent = (
    <form onSubmit={handleSubmit}>
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="ti ti-check me-2"></i>
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
          ></button>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="ti ti-alert-triangle me-2"></i>
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
          ></button>
        </div>
      )}

      <div className="row">
        {/* Title */}
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Title <span className="text-danger">*</span>
          </label>
          <CommonSelectV2
            options={TITLE_OPTIONS}
            value={selectedTitle}
            onChange={(value) => {
              const option = TITLE_OPTIONS.find(opt => opt.value === value);
              if (option) setSelectedTitle(option);
            }}
            disabled={isSubmitting}
            placeHolder="Select Title"
          />
        </div>

        {/* Priority */}
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Priority <span className="text-danger">*</span>
          </label>
          <CommonSelectV2
            options={PRIORITY_OPTIONS}
            value={selectedPriority}
            onChange={(value) => {
              const option = PRIORITY_OPTIONS.find(opt => opt.value === value);
              if (option) setSelectedPriority(option);
            }}
            disabled={isSubmitting}
            placeHolder="Select Priority"
          />
        </div>
      </div>

      {/* Subject */}
      <div className="mb-3">
        <label className="form-label">
          Subject <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          placeholder="Brief description of your issue"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>

      {/* Description */}
      <div className="mb-3">
        <label className="form-label">
          Description <span className="text-danger">*</span>
        </label>
        <textarea
          className="form-control"
          rows={5}
          placeholder="Please provide detailed information about your issue..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          disabled={isSubmitting}
        ></textarea>
        <div className="form-text">
          Please be as specific as possible to help us resolve your issue quickly.
        </div>
      </div>

      {/* File Upload */}
      <div className="mb-3">
        <label className="form-label">Attachments (Optional)</label>
        <input
          type="file"
          className="form-control"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileChange}
          ref={fileInputRef}
          disabled={isSubmitting}
        />
        <div className="form-text">
          You can upload up to 2 files (screenshots, documents, etc.)
        </div>
        
        {/* Display selected files */}
        {files && files.length > 0 && (
          <div className="mt-2">
            <div className="d-flex align-items-center justify-content-between">
              <span className="small text-muted">
                {files.length} file(s) selected
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={removeFiles}
                disabled={isSubmitting}
              >
                <i className="ti ti-x me-1"></i>
                Remove
              </button>
            </div>
            <div className="mt-1">
              {Array.from(files).map((file, index) => (
                <div key={index} className="small text-muted">
                  <i className="ti ti-file me-1"></i>
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </form>
  );

  const modalFooterContent = (
    <div className="gap-2 d-flex">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Creating...
          </>
        ) : (
          <>
            <i className="ti ti-plus me-2"></i>
            Create Ticket
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <CustomModal
        show={show}
        onClose={handleClose}
        title={modalTitle}
        bodyContent={modalBodyContent}
        footerContent={modalFooterContent}
        size="lg"
      />
    </>

  );
};

export default CreateTicketModal;
